import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Home, Search, Heart, MessageCircle, User, LogOut, Crown, Shield, Globe, Bell, X, LifeBuoy, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import SupportWidget from "@/components/SupportWidget";
import api from "@/lib/api";

const navItems = [
  { to: "/app",            label: "Home",      icon: Home,          exact: true },
  { to: "/app/browse",     label: "Browse",    icon: Search },
  { to: "/app/interests",  label: "Interests", icon: Heart },
  { to: "/app/chat",       label: "Chat",      icon: MessageCircle },
  { to: "/app/profile",    label: "Profile",   icon: User },
];

const PAGE_TITLES = {
  "/app":            "Hamarsathi",
  "/app/browse":     "Find Match",
  "/app/interests":  "Interests",
  "/app/chat":       "Messages",
  "/app/profile":    "My Profile",
  "/app/shortlist":  "Shortlist",
  "/app/kundli":     "Kundli",
  "/app/biodata":    "Bio-Data",
  "/app/viewers":    "Profile Views",
  "/app/premium":    "Go Premium",
  "/app/verify":     "Verification",
  "/app/privacy":    "Privacy",
};

// ── In-App Notification Bell ──────────────────────────────
function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const panelRef = useRef(null);

  // Fetch initial notifications from DB on mount
  useEffect(() => {
    if (!user) return;
    const fetchNotifs = async () => {
      try {
        const res = await api.get("/notifications");
        const list = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
        const formatted = list.map((doc) => ({
          id: doc.notification_id || doc._id || String(Math.random()),
          title: doc.title,
          body: doc.text || doc.body || "",
          read: doc.read,
          time: doc.created_at ? new Date(doc.created_at) : new Date(),
          icon: doc.icon || null,
          link: doc.link || "/app/interests",
        }));
        setNotifications(formatted);
      } catch (err) {
        console.error("FCM: Failed to fetch notifications:", err);
      }
    };
    fetchNotifs();
  }, [user]);

  // Listen for foreground FCM messages dispatched as custom DOM events
  useEffect(() => {
    const handler = (e) => {
      const { title, body, icon } = e.detail || {};
      const newNotif = {
        id: Date.now(),
        title: title || "Hamarsathi",
        body: body || "",
        icon,
        read: false,
        time: new Date(),
      };
      setNotifications((prev) => [newNotif, ...prev].slice(0, 30));
      setAnimate(true);
      setTimeout(() => setAnimate(false), 1000);
    };
    window.addEventListener("fcm_foreground_message", handler);
    return () => window.removeEventListener("fcm_foreground_message", handler);
  }, []);

  // Also listen for service worker postMessage (reliable fallback)
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const swHandler = (event) => {
      if (event.data?.type === "FCM_PUSH") {
        const { title, body, icon } = event.data;
        const newNotif = {
          id: Date.now(),
          title: title || "Hamarsathi",
          body: body || "",
          icon,
          read: false,
          time: new Date(),
        };
        setNotifications((prev) => [newNotif, ...prev].slice(0, 30));
        setAnimate(true);
        setTimeout(() => setAnimate(false), 1000);
      }
    };
    navigator.serviceWorker.addEventListener("message", swHandler);
    return () => navigator.serviceWorker.removeEventListener("message", swHandler);
  }, []);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const t = setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 100);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        // Mark all as read when opening dropdown
        const hasUnread = notifications.some((n) => !n.read);
        if (hasUnread) {
          api.post("/notifications/read-all").catch((err) => console.error(err));
          setNotifications((prevNotifs) => prevNotifs.map((n) => ({ ...n, read: true })));
        }
      }
      return next;
    });
  };

  const unread = notifications.filter((n) => !n.read).length;

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        className={`rounded-full relative transition-transform duration-200 ${animate ? "scale-125" : ""}`}
        onClick={handleToggle}
        aria-label="Notifications"
        data-testid="notif-bell-btn"
      >
        <Bell
          className={`w-[18px] h-[18px] ${unread > 0 ? "text-primary font-bold" : "text-muted-foreground"}`}
          strokeWidth={2}
        />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none animate-bounce">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-[99998] bg-black/10 md:hidden" onClick={() => setOpen(false)} />
          <div className="fixed right-3 left-3 top-16 md:absolute md:right-0 md:left-auto md:w-80 md:top-11 max-h-[75vh] md:max-h-96 overflow-y-auto rounded-2xl shadow-2xl border border-border/60 bg-card z-[99999] animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/40 sticky top-0 bg-card">
              <span className="font-semibold text-sm flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" /> Notifications
              </span>
              {notifications.length > 0 && (
                <button
                  onClick={() => setNotifications([])}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors font-medium"
                >
                  Clear all
                </button>
              )}
            </div>

            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                <Bell className="w-8 h-8 opacity-20" />
                <span className="text-sm">No notifications yet</span>
              </div>
            ) : (
              <ul>
                {notifications.map((n) => (
                  <li
                    key={n.id}
                    onClick={() => {
                      setOpen(false);
                      if (n.link) window.location.href = n.link;
                    }}
                    className={`flex gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors border-b border-border/20 last:border-0 relative ${
                      !n.read ? "bg-primary/5" : ""
                    }`}
                  >
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      {n.icon ? (
                        <img src={n.icon} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <Bell className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{n.title}</p>
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">
                        {n.time.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const pageTitle = PAGE_TITLES[location.pathname] || "Hamarsathi";

  return (
    <div className="min-h-screen flex flex-col bg-background">

      {/* ════════════════════════════════════════════
          DESKTOP HEADER — visible only on md+
          ════════════════════════════════════════════ */}
      <header className="hidden md:block sticky top-0 z-40 glass border-b border-border/60">
        <div className="container-px h-16 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/logo.png" alt="Hamarsathi" className="h-10 object-contain" />
          </Link>

          <nav className="flex items-center gap-1">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.exact}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted"
                  }`
                }
              >
                {t("nav." + it.label.toLowerCase())}
              </NavLink>
            ))}
            <NavLink to="/app/kundli" data-testid="nav-kundli" className={({ isActive }) =>
              `px-4 py-2 rounded-full text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted"}`
            }>{t("nav.kundli")}</NavLink>
            <NavLink to="/app/biodata" data-testid="nav-biodata" className={({ isActive }) =>
              `px-4 py-2 rounded-full text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted"}`
            }>Bio-Data</NavLink>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("open_support_chat"))}
              className="px-4 py-2 rounded-full text-sm transition-colors text-foreground/80 hover:bg-muted flex items-center gap-1.5"
            >
              <LifeBuoy className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <span>Help & Support</span>
            </button>
          </nav>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm"
              onClick={() => setLanguage(language === "en" ? "hi" : "en")}
              className="rounded-full flex items-center gap-1.5 px-3 hover:bg-muted"
              data-testid="lang-switch-btn"
            >
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase">{language === "en" ? "हिं" : "EN"}</span>
            </Button>

            {user?.role === "admin" && (
              <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="nav-admin-btn">
                <Link to="/admin"><Shield className="w-4 h-4 mr-1" strokeWidth={1.5}/>{t("nav.admin")}</Link>
              </Button>
            )}
            {user?.is_mitra && (
              <Button asChild variant="outline" size="sm" className="rounded-full border-amber-500/30 text-amber-700 hover:bg-amber-50" data-testid="nav-mitra-btn">
                <Link to="/mitra"><Award className="w-4 h-4 mr-1 text-amber-600" strokeWidth={1.5}/>{language === "hi" ? "मित्र डैशबोर्ड" : "Mitra Dashboard"}</Link>
              </Button>
            )}
            {user?.is_premium ? (
              <span className="inline-flex items-center gap-1 text-xs bg-secondary/15 text-secondary px-3 py-1 rounded-full font-medium">
                <Crown className="w-3 h-3" strokeWidth={2} /> {t("nav.premium")}
              </span>
            ) : (
              <Button asChild variant="outline" size="sm" className="rounded-full border-secondary/30 text-secondary hover:bg-secondary/10" data-testid="nav-upgrade-btn">
                <Link to="/app/premium"><Crown className="w-4 h-4 mr-1" strokeWidth={1.5} />{t("nav.premium")}</Link>
              </Button>
            )}
            <Button asChild variant="ghost" size="icon" className="rounded-full" data-testid="nav-privacy-btn" aria-label="Privacy Settings">
              <Link to="/app/privacy"><Shield className="w-4 h-4" strokeWidth={1.5} /></Link>
            </Button>

            {/* 🔔 Notification Bell — Desktop */}
            <NotificationBell />

            <Button variant="ghost" size="icon" className="rounded-full"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="logout-btn" aria-label="Logout"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      {/* ════════════════════════════════════════════
          MOBILE TOP HEADER — visible only below md
          ════════════════════════════════════════════ */}
      <header className="md:hidden sticky top-0 z-40 mobile-header w-full max-w-full">
        <div className="flex items-center justify-between px-3 h-14 w-full">
          {/* Left: Brand Logo */}
          <Link to="/app" className="flex items-center shrink-0" data-testid="mob-nav-logo">
            <img src="/logo.png" alt="Hamarsathi" className="h-7 object-contain max-w-[130px]" />
          </Link>

          {/* Right: Clean Action Buttons */}
          <div className="flex items-center gap-1 shrink-0">
            {user?.role === "admin" && (
              <Button asChild variant="ghost" size="icon" className="rounded-full w-8 h-8 p-0">
                <Link to="/admin"><Shield className="w-4 h-4 text-primary" strokeWidth={1.8}/></Link>
              </Button>
            )}
            {user?.is_mitra && (
              <Button asChild variant="ghost" size="icon" className="rounded-full w-8 h-8 p-0">
                <Link to="/mitra"><Award className="w-4 h-4 text-amber-600" strokeWidth={1.8}/></Link>
              </Button>
            )}

            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setLanguage(language === "en" ? "hi" : "en")}
              className="rounded-full h-8 px-2 text-xs font-bold text-muted-foreground hover:text-foreground border border-border/40"
            >
              {language === "en" ? "हिं" : "EN"}
            </Button>

            {/* 🔔 Notification Bell */}
            <NotificationBell />

            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full w-8 h-8 p-0 text-muted-foreground hover:text-foreground"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="mob-logout-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.8} />
            </Button>
          </div>
        </div>
      </header>

      {/* ════════════════════════════════════════════
          PAGE CONTENT
          ════════════════════════════════════════════ */}
      <main className="flex-1 md:pb-12 mobile-main">
        {children}
      </main>

      {/* ════════════════════════════════════════════
          MOBILE BOTTOM TAB BAR — visible only below md
          ════════════════════════════════════════════ */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 mobile-tab-bar">
        <div className="flex items-center justify-around px-1" style={{ height: "60px" }}>
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.exact}
              data-testid={`mobnav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `mobile-tab-item${isActive ? " mobile-tab-item--active" : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`mobile-tab-icon${isActive ? " mobile-tab-icon--active" : ""}`}>
                    <it.icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2 : 1.5} />
                  </span>
                  <span className={`mobile-tab-label${isActive ? " mobile-tab-label--active" : ""}`}>
                    {t("nav." + it.label.toLowerCase())}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>
        {/* iPhone home bar safe area */}
        <div className="mobile-safe-fill" />
      </nav>

      <SupportWidget user={user} />
    </div>
  );
}
